import { wrapApiCall } from '@/lib/axios'
import type { ReportLang } from '@/utils/helper/reportLang'
import type { AxiosInstance } from 'axios'
import {
  GetRevenueBreakdownSummaryParams,
  RevenueBreakdownFacility,
  RevenueBreakdownGroupClass,
  RevenueBreakdownKpis,
  RevenueBreakdownMembership,
  RevenueBreakdownPersonalTraining,
  RevenueAlertThresholds,
  RevenueBreakdownProductSales,
  RevenueBreakdownSummary,
  RevenueBreakdownSwimming,
  UpdateRevenueAlertThresholdsPayload,
} from './model'

export const RevenueBreakdownService = (client: AxiosInstance) => {
  return {
    async getSummary(params: GetRevenueBreakdownSummaryParams): Promise<RevenueBreakdownSummary> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueBreakdownSummary }>('/revenue-breakdown/summary', {
          params,
        })
      )
      return data.data
    },

    async getKpis(params: GetRevenueBreakdownSummaryParams): Promise<RevenueBreakdownKpis> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueBreakdownKpis }>('/revenue-breakdown/kpis', {
          params,
        })
      )
      return data.data
    },

    async getMembershipCard(
      params: GetRevenueBreakdownSummaryParams
    ): Promise<RevenueBreakdownMembership> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueBreakdownMembership }>('/revenue-breakdown/membership', {
          params,
        })
      )
      return data.data
    },

    async getPersonalTrainingCard(
      params: GetRevenueBreakdownSummaryParams
    ): Promise<RevenueBreakdownPersonalTraining> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueBreakdownPersonalTraining }>(
          '/revenue-breakdown/personal-training',
          { params }
        )
      )
      return data.data
    },

    async getGroupClassCard(
      params: GetRevenueBreakdownSummaryParams
    ): Promise<RevenueBreakdownGroupClass> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueBreakdownGroupClass }>('/revenue-breakdown/group-class', {
          params,
        })
      )
      return data.data
    },

    async getSwimmingCard(
      params: GetRevenueBreakdownSummaryParams
    ): Promise<RevenueBreakdownSwimming> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueBreakdownSwimming }>('/revenue-breakdown/swimming', {
          params,
        })
      )
      return data.data
    },

    async getProductSalesCard(
      params: GetRevenueBreakdownSummaryParams
    ): Promise<RevenueBreakdownProductSales> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueBreakdownProductSales }>('/revenue-breakdown/product-sales', {
          params,
        })
      )
      return data.data
    },

    async getFacilityCard(
      params: GetRevenueBreakdownSummaryParams
    ): Promise<RevenueBreakdownFacility> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueBreakdownFacility }>('/revenue-breakdown/facility', { params })
      )
      return data.data
    },

    /**
     * QTY-1745 — Read the branch's resolved alert thresholds (+ canConfigure).
     */
    async getAlertThresholds(): Promise<RevenueAlertThresholds> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data: RevenueAlertThresholds }>('/revenue-breakdown/alert-thresholds')
      )
      return data.data
    },

    /**
     * QTY-1745 — Persist the branch's alert thresholds (Owner/Manager only;
     * gated server-side). Returns the freshly-resolved thresholds.
     */
    async updateAlertThresholds(
      payload: UpdateRevenueAlertThresholdsPayload
    ): Promise<RevenueAlertThresholds> {
      const { data } = await wrapApiCall(() =>
        client.put<{ data: RevenueAlertThresholds }>('/revenue-breakdown/alert-thresholds', payload)
      )
      return data.data
    },

    /**
     * Download the role-gated revenue breakdown as one Excel workbook — the
     * summary, the per-service cards and (with CRM Sale Report READ) the
     * receipt lines the money came from. Role gating is applied server-side, so
     * the file never contains metrics the current role may not see.
     */
    async downloadRevenueReportWorkbook(
      params: GetRevenueBreakdownSummaryParams & { lang: ReportLang }
    ): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/revenue-breakdown/export/workbook', {
          params,
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )
      return data
    },
  }
}
